'use client';

import { useEffect, useMemo } from 'react';

import usePayment from '@/components/UIComponents/CommonModal/payment/us/hook/usePayment';
import { useTranslate } from '@/hooks/useTranslate';
import UserStore from '@/store/User';
import { AIFunction } from '@/utils/const';
import { EventNewID, SubscribeReasonEnum } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { t } from '@/utils/helper/intl';

interface FirstOrderModalContentProps {
  onClose: () => void;
  onPayEnd: () => void;
}

function splitCountdown(seconds: number): { h: string; m: string; s: string } {
  const s = Math.max(0, seconds);
  return {
    h: String(Math.floor(s / 3600)).padStart(2, '0'),
    m: String(Math.floor((s % 3600) / 60)).padStart(2, '0'),
    s: String(s % 60).padStart(2, '0'),
  };
}

const CreditsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.8 2.85C2.2625 3.1125 2 3.4 2 3.7125V4.575C2 4.8875 2.2625 5.175 2.8 5.4375C3.3375 5.7 4.0625 5.9125 4.9875 6.0625C5.9125 6.2125 6.9125 6.2875 8 6.2875C9.0875 6.2875 10.0875 6.2125 11.0125 6.0625C11.9375 5.9125 12.6625 5.7 13.2 5.4375C13.7375 5.175 14 4.8875 14 4.575V3.7125C14 3.4 13.7375 3.1125 13.2 2.85C12.6625 2.5875 11.9375 2.375 11.0125 2.225C10.0875 2.075 9.0875 2 8 2C6.9125 2 5.9125 2.075 4.9875 2.225C4.075 2.3875 3.3375 2.5875 2.8 2.85ZM2 8.625V9.7125C2 10.025 2.2625 10.3125 2.8 10.575C3.3375 10.8375 4.0625 11.05 4.9875 11.2C5.9125 11.35 6.9125 11.425 8 11.425C9.0875 11.425 10.0875 11.35 11.0125 11.2C11.9375 11.05 12.6625 10.8375 13.2 10.575C13.7375 10.3125 14 10.025 14 9.7125V8.575C13.375 8.95 12.5375 9.2375 11.4625 9.425C10.3875 9.625 9.2375 9.7125 8 9.7125C6.7625 9.7125 5.6125 9.625 4.5375 9.425C3.4625 9.2375 2.625 8.95 2 8.575M2 11.125V12.2875C2 12.6 2.2625 12.8875 2.8 13.15C3.3375 13.4125 4.0625 13.625 4.9875 13.775C5.9125 13.925 6.9125 14 8 14C9.0875 14 10.0875 13.925 11.0125 13.775C11.9375 13.625 12.6625 13.4125 13.2 13.15C13.7375 12.8875 14 12.6 14 12.2875V11.15C13.375 11.525 12.5375 11.8125 11.4625 12C10.3875 12.1875 9.2375 12.2875 8 12.2875C6.7625 12.2875 5.6125 12.1875 4.5375 12C3.4625 11.8125 2.625 11.525 2 11.15M2 6V7.1375C2 7.45 2.2625 7.7375 2.8 8C3.3375 8.2625 4.0625 8.475 4.9875 8.625C5.9125 8.775 6.9125 8.85 8 8.85C9.0875 8.85 10.0875 8.775 11.0125 8.625C11.9375 8.475 12.6625 8.2625 13.2 8C13.7375 7.7375 14 7.45 14 7.1375V6C13.375 6.375 12.5375 6.6625 11.4625 6.85C10.3875 7.0375 9.2375 7.1375 8 7.1375C6.7625 7.1375 5.6125 7.0375 4.5375 6.85C3.4625 6.6625 2.625 6.375 2 6Z"
      fill="#4931FA"
    />
  </svg>
);

const FeaturesIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.22265 2.00646C4.22027 2.43978 1.88049 5.08096 2.00473 8.21736C2.12896 11.3125 4.67581 13.8918 7.78171 13.995C10.929 14.1188 13.5587 11.9522 13.9935 8.9602C14.0556 8.50624 13.6622 8.21736 13.186 8.21736H8.48572C8.21654 8.21736 8.21654 7.76341 8.21654 7.49517V2.79056C8.21654 2.33661 7.69889 1.94456 7.22265 2.00646Z"
      fill="#4931FA"
    />
    <path
      d="M9 2.64855V6.56831C9 6.79279 9.20665 7 9.43053 7H13.3397C13.753 7 14.0458 6.62011 13.9941 6.22296C13.6669 4.04723 11.9448 2.33774 9.77495 2.00965C9.37886 1.94058 9 2.2514 9 2.64855Z"
      fill="#4931FA"
      fillOpacity="0.9"
    />
  </svg>
);

const ModelsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.0104 3.98828C15.2355 3.50833 15.0081 2.94494 14.5007 2.73123L8.77194 0.163422C8.52827 0.0555953 8.26475 -0.000104755 7.99828 -0.000104755C7.73182 -0.000104755 7.4683 0.0555953 7.22463 0.163422L1.49703 2.73123C1.27386 2.82319 1.09269 2.99459 0.988502 3.21233C0.763377 3.69231 0.990784 4.2557 1.49703 4.46939L7.52403 7.1572C7.67031 7.23033 7.83258 7.27148 8.00058 7.27377C8.16536 7.26789 8.32717 7.22822 8.47597 7.1572L14.503 4.46939C14.7253 4.376 14.9058 4.20446 15.0104 3.98714M6.88294 7.99942L1.44903 5.58245C1.30464 5.50552 1.14354 5.46529 0.979932 5.46529C0.816323 5.46529 0.655225 5.50552 0.51083 5.58245C0.358486 5.66474 0.23084 5.78616 0.141048 5.9342C0.0512572 6.08225 0.00256909 6.25156 2.34375e-06 6.42469V12.5923C-0.000405448 12.7765 0.0515947 12.9571 0.149934 13.1129C0.248274 13.2687 0.388897 13.3933 0.555393 13.4722L6.00071 15.8926C6.13842 15.9615 6.29012 15.9978 6.44411 15.9989C6.61462 15.9929 6.78103 15.945 6.92864 15.8594C7.07913 15.7754 7.20503 15.6534 7.29384 15.5057C7.38264 15.358 7.43127 15.1896 7.43489 15.0172V8.84166C7.42621 8.66484 7.37047 8.49356 7.27343 8.34549C7.1764 8.19743 7.04159 8.07796 6.88294 7.99942ZM15.9989 12.5934V6.42697C15.9957 6.25397 15.9468 6.0849 15.857 5.93696C15.7673 5.78902 15.64 5.6675 15.488 5.58475C15.348 5.50681 15.191 5.46411 15.0308 5.46032C14.8705 5.45654 14.7117 5.49179 14.5681 5.56303L9.0965 7.99944C8.94056 8.0797 8.80893 8.20024 8.71529 8.34854C8.62164 8.49684 8.56939 8.66749 8.56396 8.8428V15.0366C8.56853 15.3806 8.75938 15.6995 9.07021 15.8789C9.21877 15.9577 9.38561 16 9.55474 16C9.70902 16 9.86216 15.9657 9.99814 15.8937L15.4652 13.4722C15.6274 13.3903 15.7635 13.2645 15.8578 13.1092C15.9522 12.9538 16.001 12.7751 15.9989 12.5934Z"
      fill="#4931FA"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.9499 15.95L6.04987 6.04999M6.04987 15.95L15.9499 6.04999"
      stroke="white"
      strokeOpacity="0.8"
      strokeWidth="2.0625"
      strokeLinecap="round"
    />
  </svg>
);

const BENEFITS = [
  {
    key: 'credits',
    Icon: CreditsIcon,
    label: (
      <span>
        <span>{t('firstOrder.benefit.credits')}</span>
        <div>{t('firstOrder.benefit.videos')}</div>
      </span>
    ),
  },
  { key: 'features', Icon: FeaturesIcon, label: t('Everything-in-Pro') },
  { key: 'models', Icon: ModelsIcon, label: t('firstOrder.benefit.models') },
] as const;

const FirstOrderModalContent = ({ onClose, onPayEnd }: FirstOrderModalContentProps) => {
  const { t } = useTranslate();
  const couponInfo = UserStore((store) => store.couponInfo);
  const getCurrentDiscountInfo = UserStore((store) => store.getCurrentDiscountInfo);
  const newUserCountDownTime = UserStore((store) => store.newUserCountDownTime);

  const { handlePay, formatData, isPurchaseSuccess } = usePayment({
    t,
    feature: AIFunction.Other,
    defaultActiveTab: 'month',
    reason: SubscribeReasonEnum.twentyFourHourPop,
  });

  useEffect(() => {
    trackEvent(EventNewID.TWENTY_FOUR_HOUR_POP_EXP, {});
  }, []);

  useEffect(() => {
    if (isPurchaseSuccess) {
      onPayEnd();
    }
  }, [isPurchaseSuccess]);

  const targetCommodity = useMemo(() => {
    const advancedEntry = formatData.find(([key]) => key === 'advancedVip');
    if (!advancedEntry) return null;
    const monthData: any[] = advancedEntry[1]?.commodityData?.month || [];
    if (!monthData.length) return null;
    if (couponInfo?.type === 'ticket' && couponInfo.availabled?.length) {
      const applicableGoods = couponInfo.availabled.flatMap(
        (c: any) => c.applicableGoods,
      );
      const matched = monthData.find((c: any) =>
        applicableGoods.includes(c.commodityCode),
      );
      if (matched) return matched;
    }
    return monthData[0];
  }, [formatData, couponInfo]);

  const priceInfo = useMemo(() => {
    if (!targetCommodity) return null;
    const originalPrice = Number(targetCommodity.originalPrice / 100) || 0;
    const realPrice = Number(targetCommodity.realPrice / 100) || 0;
    const { realPrice: discountedPrice } = getCurrentDiscountInfo(
      realPrice,
      targetCommodity.commodityCode,
    );
    const finalPrice = discountedPrice || realPrice;
    const discountPct = originalPrice
      ? Math.round((1 - finalPrice / originalPrice) * 100)
      : 0;
    const currency = targetCommodity?.content?.currency === 'CNY' ? '¥' : '$';
    return { originalPrice, discountedPrice: finalPrice, discountPct, currency };
  }, [targetCommodity, couponInfo, getCurrentDiscountInfo]);

  const handleUpgrade = async () => {
    if (!targetCommodity) return;
    const advancedEntry = formatData.find(([key]) => key === 'advancedVip');
    if (!advancedEntry) return;
    await handlePay({
      key: 'advancedVip',
      item: advancedEntry[1],
      btnIndex: 0,
      feature: AIFunction.Other,
      price: String(targetCommodity.realPrice),
      currency: targetCommodity?.content?.currency || 'USD',
      selectedCommodityCode: targetCommodity.commodityCode,
    });
  };

  const { h, m, s } = splitCountdown(newUserCountDownTime);

  return (
    <div className="relative flex flex-col items-center">
      {/* 卡片 */}
      <div className="relative" style={{ width: 437, height: 418 }}>
        {/* 锯齿形状背景 SVG */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 437 418"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="firstOrderGrad"
              x1="437"
              y1="262.5"
              x2="0"
              y2="114.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#C35CFF" />
              <stop offset="1" stopColor="#4D2FFF" />
            </linearGradient>
          </defs>
          <path
            d="M425.5 418L414 404L402.5 418L391 404L379.5 418L368 404L356.5 418L345 404L333.5 418L322 404L310.5 418L299 404L287.5 418L276 404L264.5 418L253 404L241.5 418L230 404L218.5 418L207 404L195.5 418L184 404L172.5 418L161 404L149.5 418L138 404L126.5 418L115 404L103.5 418L92 404L80.5 418L69 404L57.5 418L46 404L34.5 418L23 404L11.5 418L0 404V14L11.5 0L23 14L34.5 0L46 14L57.5 0L69 14L80.5 0L92 14L103.5 0L115 14L126.5 0L138 14L149.5 0L161 14L172.5 0L184 14L195.5 0L207 14L218.5 0L230 14L241.5 0L253 14L264.5 0L276 14L287.5 0L299 14L310.5 0L322 14L333.5 0L345 14L356.5 0L368 14L379.5 0L391 14L402.5 0L414 14L425.5 0L437 14V404L425.5 418Z"
            fill="url(#firstOrderGrad)"
          />
        </svg>
        {/* 内容层 */}
        <div
          className="relative z-10 flex flex-col gap-[22px] items-center px-[40px]"
          style={{ paddingTop: 40, paddingBottom: 32 }}
        >
          {/* 关闭按钮 — 绝对定位在卡片右上角 */}
          <button
            onClick={onClose}
            className="absolute top-[16px] right-[8px] z-20 p-[8px] flex items-center justify-center cursor-pointer bg-transparent border-none rounded-[8px] hover:bg-white/10"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
          {/* 标题 */}
          <div className="flex gap-[8px] items-center" style={{ lineHeight: '34px' }}>
            {priceInfo && priceInfo.discountPct > 0 && (
              <span className="text-[24px] font-bold" style={{ color: '#FFEE00' }}>
                {priceInfo.discountPct}% OFF
              </span>
            )}
            <span className="text-[24px] font-semibold text-white">
              {t('firstOrder.title.plan', { defaultMessage: 'Month PRO PLAN' })}
            </span>
          </div>

          {/* 倒计时三格 */}
          {newUserCountDownTime > 0 && (
            <div className="flex gap-[8px] items-center">
              {[h, m, s].map((val, i) => (
                <>
                  <div
                    key={i}
                    className="bg-white flex items-center justify-center rounded-[4px] select-none"
                    style={{ width: 54, height: 54 }}
                  >
                    <span className="font-gabarito font-medium text-[36px] leading-[24px] text-black">
                      {val}
                    </span>
                  </div>
                  {i < 2 && (
                    <span className="text-[20px] font-bold text-white leading-[28px]">
                      :
                    </span>
                  )}
                </>
              ))}
            </div>
          )}

          {/* 价格胶囊 */}
          {priceInfo && (
            <div
              className="flex gap-[4px] items-center px-[12px] py-[2px] rounded-[36px]"
              style={{ background: 'rgba(0,0,0,0.24)' }}
            >
              <span
                className="text-[16px] font-medium line-through leading-[22px]"
                style={{ color: 'rgba(255,255,255,0.8)' }}
              >
                {priceInfo.currency}
                {priceInfo.originalPrice}
              </span>
              <span
                className="text-[24px] font-semibold leading-[34px]"
                style={{ color: '#FEF974' }}
              >
                {priceInfo.currency}
                {priceInfo.discountedPrice}
              </span>
              <span
                className="text-[16px] font-medium leading-[22px]"
                style={{ color: '#FEF974' }}
              >
                / {t('bc.month')}
              </span>
            </div>
          )}

          {/* 权益图标行 */}
          <div className="flex gap-[16px] items-start">
            {BENEFITS.map(({ key, Icon, label }) => (
              <div key={key} className="flex flex-col gap-[8px] items-center w-[102px]">
                <div
                  className="bg-white flex items-center justify-center p-[8px] rounded-full overflow-hidden"
                  style={{ width: 32, height: 32 }}
                >
                  <Icon />
                </div>
                <p
                  className="text-[12px] text-center leading-[16px]"
                  style={{ color: 'rgba(255,255,255,0.8)' }}
                >
                  {label}
                </p>
              </div>
            ))}
          </div>

          {/* Upgrade 按钮 */}
          <div className="w-[354px]">
            <button
              onClick={handleUpgrade}
              className="relative w-full h-[46px] rounded-[46px] text-white text-[18px] font-semibold cursor-pointer border-none flex items-center justify-center gap-[10px]"
              style={{ background: '#000000' }}
            >
              {t('firstOrder.btn.upgrade', { defaultMessage: 'Upgrade' })}
              <svg
                className="absolute right-[20px] top-1/2 -translate-y-1/2"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M5 12H19M19 12L13 6M19 12L13 18"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstOrderModalContent;
